import InputValidator from "./inputValidator";

export default class FormValidator {

    constructor(form, options = {}) {
        this.validators = {
            required: (input) =>  this.inputValidator.validateEmpty(input),
            email: (input) => this.inputValidator.validateEmail(null, input, null),
            disabled: (input) => this.inputValidator.validateDisabledSelect(input),
            number: (input) => this.inputValidator.validateNumber(input),
            textAlphabetic: (input) => this.inputValidator.validateTextAlphabetic(input),
            checked: (input) => this.inputValidator.validateIsChecked(input),
            checkFileSize: (input) => this.inputValidator.validateFileSize(input),
            checkFileRequired: (input) => this.inputValidator.validateFileEmpty(input),
            dni: (input) => this.inputValidator.validateDni(input),
            nie: (input) => this.inputValidator.validateNie(input),
            dateFormat: (input) => this.inputValidator.validateDateFormat(null, input),
            birthday: (input) => this.inputValidator.validateBirthday(input),
            iban: (ibanInput, swiftInput) => this.inputValidator.validateIban(ibanInput, swiftInput),
            swift: (swiftInput) => this.inputValidator.validateSwift(swiftInput)
        }
        this.invalidInputs = new Map();
        if (!form instanceof HTMLFormElement) throw new Error('FormValidator: form param must be a form tag element');
        this.inputValidator = new InputValidator();
        this.form = form;
        form.validator = this;

        if (options.customValidators) {
            this.addValidators(options.customValidators);
        }

        this.eventDelegation();
    }

    eventDelegation() {
        if (this.form.dataset.validateOn) {
            const validateEvents = this.form.dataset.validateOn.split('|');
            validateEvents.forEach(eventName => {
                this.form.addEventListener(eventName, (event) => {
                    const isValid = this.validateForm();
                    if (!isValid) {
                        event.preventDefault();
                    } else if (eventName == 'submit' && this.form.dataset.preventSubmit) {
                        if (this.form.dataset.preventSubmit == 'always') {
                            event.preventDefault();
                        } else if (this.form.dataset.preventSubmit == 'errors' && this.getErrorCount() > 0) {
                            event.preventDefault();
                        }
                    }
                    this.form.dispatchEvent(
                        new CustomEvent('submitedFormValidated', {
                            detail: {
                                isValid: this.getErrorCount() === 0
                            }
                        }
                    ));
                });
            });
        }

        this.getInputs().forEach(input => {
            if (!input.dataset.validateOn) return;
            const validateEvents = input.dataset.validateOn.split('|');
            validateEvents.forEach(event => {
                input.addEventListener(event, (event) => {
                    this.validateInput(input);
                });
            });
        });

        document.querySelectorAll('[data-action="validate-form"]').forEach(button => {
            button.addEventListener('click', (event) => {
                if (button.dataset.preventSubmit) event.preventDefault();
                this.validateForm();
            });
        })
    }

    validateForm() {
        const inputs = this.getInputs();
        inputs.forEach(input => {
            this.validateInput(input);
        });
        this.form.dispatchEvent(
            new CustomEvent('formValidated', {
                detail: {
                    isValid: this.getErrorCount() === 0
                }
            })
        );
        return this.getErrorCount() === 0;
    }

    validateInput(input, validation = null) {
        if (typeof input === 'string') {
            const inputName = input;
            input = this.form.querySelector(`[name="${input}"]`);
            if (!input) throw new Error(`FormValidator: input with name ${inputName} not found`);
        }
        if (!input.dataset.validation) return;
        const validations = input.dataset.validation.split('|');
        if (validation) {
            validations.push(validation);
        }
        let failedValidator = null;
        const hasError = validations.some(validation => {
            let result;
            if (!this.validators[validation]) throw new Error(`FormValidator: ${validation} is not a valid validation type`);

            const extraInput = input.getAttribute('data-extra-input');
            let extraInputElement = null;
            if (extraInput) {
                extraInputElement = this.form.querySelector(`[name="${extraInput}"]`);
            }
            result = this.validators[validation](input, extraInputElement);
            if (!result) failedValidator = validation;
            return !result;
        });

        if (hasError) {
            input.classList.add('border-error');
            this.invalidInputs.set(input, failedValidator);
        } else {
            input.classList.remove('border-error');
            this.invalidInputs.delete(input);
        }

        input.dispatchEvent(
            new CustomEvent('inputValidated', {
                detail: {
                    isValid: !hasError,
                    failedValidator: failedValidator
                }
            })
        );

        return !hasError;
    }

    getInputs() {
        return this.form.querySelectorAll('[data-validation]');
    }

    getInvalidInputs() {
        return Array.from(this.invalidInputs.keys()).filter(input => !input.dataset.disabledValidation);
    }

    getErrorCount() {
        return this.getInvalidInputs().length;
    }

    toggleInputValidation(input) {
        input.dataset.validation ?
            this.disableInputValidation(input) :
            this.enableInputValidation(input);
    }

    disableInputValidation(input) {
        if (!input.dataset.validation) return;
        input.dataset.disabledValidation = input.dataset.validation;
        delete input.dataset.validation;
    }

    enableInputValidation(input) {
        if (input.dataset.validation || !input.dataset.disabledValidation) return;
        input.dataset.validation = input.dataset.disabledValidation;
        delete input.dataset.disabledValidation;
    }

    addValidators(validators) {
        Object.keys(validators).forEach(validator => {
            this.validators[validator] = validators[validator];
        });
    }
}